import { useCallback, useMemo, useRef, useState } from 'react';
import './delivery.css';
import { I18nProvider, useI18n } from '@react/i18n';
import { trackFunnel } from '@react/funnel';
import { ApiError } from '../checkout/api/checkoutApi';
import { Spinner } from '../checkout/sections/ui';
import { createDeliveryPaymentApi } from './api/deliveryPaymentApi';
import { Branch, DeliveryPaymentProps, DeliveryPaymentState, DeliveryRow, PaymentRow } from './types';
import OptionCard from './OptionCard';
import BranchModal from './BranchModal';

/**
 * React ostrov kroku 2 košíku (doprava a platba) — náhrada Nette komponenty
 * DeliveryPayment za feature flagem %system.deliveryPaymentReact%.
 * Server je jediný zdroj pravdy: každá mutace vrací čerstvý state.
 */
export default function DeliveryPaymentStep(props: DeliveryPaymentProps) {
    return (
        <I18nProvider url={props.translationsUrl} loading={<StepSkeleton />}>
            <Step {...props} />
        </I18nProvider>
    );
}

function StepSkeleton() {
    return (
        <div className="dpr dpr--skeleton" aria-busy="true">
            <div className="dpr-skeleton dpr-skeleton--title" />
            <div className="dpr-columns">
                <div className="dpr-skeleton-rows">
                    {[0, 1, 2, 3].map((i) => <div key={i} className="dpr-skeleton dpr-skeleton--card" />)}
                </div>
                <div className="dpr-skeleton-rows">
                    {[0, 1, 2].map((i) => <div key={i} className="dpr-skeleton dpr-skeleton--card" />)}
                </div>
            </div>
        </div>
    );
}

type Section = 'delivery' | 'payment';

/** chyby z POST /continue roztříděné podle sloupce, ke kterému patří */
type SectionErrors = Record<Section, string[]>;

const NO_SECTION_ERRORS: SectionErrors = { delivery: [], payment: [] };

function parseSectionErrors(fields: unknown): SectionErrors | null {
    const sections = (fields as { sections?: Partial<Record<Section, unknown>> } | undefined)?.sections;
    if (!sections || !Array.isArray(sections.delivery) || !Array.isArray(sections.payment)) {
        return null;
    }

    return { delivery: sections.delivery as string[], payment: sections.payment as string[] };
}

interface ModalRequest {
    row: DeliveryRow;
    /** modal otevřený z nedostupné karty — potvrzení pobočky odebere konfliktní platbu */
    resolveConflict?: boolean;
}

function Step(props: DeliveryPaymentProps) {
    const t = useI18n();
    const api = useMemo(() => createDeliveryPaymentApi(props.apiBaseUrl), [props.apiBaseUrl]);

    const [state, setState] = useState<DeliveryPaymentState>(props.initialState);
    const [pendingDelivery, setPendingDelivery] = useState<number | null>(null);
    const [pendingPayment, setPendingPayment] = useState<number | null>(null);
    const [countryPending, setCountryPending] = useState(false);
    const [modal, setModal] = useState<ModalRequest | null>(null);
    const [confirmingBranch, setConfirmingBranch] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [sectionErrors, setSectionErrors] = useState<SectionErrors>(NO_SECTION_ERRORS);
    const [continuing, setContinuing] = useState(false);
    const errorsRef = useRef<HTMLDivElement>(null);
    const deliveryColRef = useRef<HTMLDivElement>(null);
    const paymentColRef = useRef<HTMLDivElement>(null);

    // klik během rozjeté mutace by pracoval se zastaralou dostupností
    // (server by pak nekompatibilní pár odmítl 409) — radši ho ignoruj
    const mutating = countryPending || confirmingBranch || pendingDelivery !== null || pendingPayment !== null;

    const failure = useCallback((e: unknown) => {
        // serverová hláška (JSON envelope) je přeložená; výpadek (5xx, HTML stránka brány
        // při deployi) nese jen technický anglický text → obecná přeložená hláška
        const message = e instanceof ApiError && e.code !== 'INVALID_RESPONSE' && e.status < 500
            ? e.message
            : t('Something went wrong. Please try again.');
        trackFunnel('CheckoutApiError', { step: 'deliveryPayment', code: e instanceof ApiError ? e.code : 'network' });
        setErrors([message]);
    }, [t]);

    // výběr ve sloupci smaže jen jeho vlastní chybu — hláška u druhého sloupce
    // (typicky „vyberte platbu" po zvolení dopravy) musí zůstat vidět
    const applyState = useCallback((next: DeliveryPaymentState, section?: Section) => {
        setState(next);
        setErrors([]);
        setSectionErrors((current) => (section ? { ...current, [section]: [] } : NO_SECTION_ERRORS));
    }, []);

    // 409 CONFLICT = klient klikl nad zastaralým stavem; výběr se na serveru
    // nezměnil, jen se tiše srovná UI s realitou (bez chybové hlášky)
    const mutationFailure = useCallback((e: unknown) => {
        if (e instanceof ApiError && e.code === 'CONFLICT') {
            api.getState().then((next) => applyState(next)).catch(() => {});

            return;
        }

        failure(e);
    }, [api, applyState, failure]);

    const handleCountry = useCallback((countryId: number) => {
        if (mutating) {
            return;
        }

        setCountryPending(true);
        api.changeCountry(countryId)
            .then((next) => applyState(next))
            .catch(failure)
            .finally(() => setCountryPending(false));
    }, [api, applyState, failure, mutating]);

    const handleDelivery = useCallback((row: DeliveryRow) => {
        if (mutating) {
            return;
        }

        const resolveConflict = !row.available;

        if (row.hasBranches) {
            setModal({ row, resolveConflict });

            return;
        }

        setPendingDelivery(row.id);
        api.selectDelivery(row.id, undefined, resolveConflict)
            .then((next) => applyState(next, 'delivery'))
            .catch(mutationFailure)
            .finally(() => setPendingDelivery(null));
    }, [api, applyState, mutationFailure, mutating]);

    const handleBranchConfirm = useCallback((branch: Branch) => {
        setConfirmingBranch(true);
        api.selectDelivery(branch.deliveryId, branch.id, modal?.resolveConflict === true)
            .then((next) => {
                applyState(next, 'delivery');
                setModal(null);
            })
            .catch((e: unknown) => {
                if (e instanceof ApiError && e.code === 'CONFLICT') {
                    setModal(null);
                }
                mutationFailure(e);
            })
            .finally(() => setConfirmingBranch(false));
    }, [api, applyState, modal, mutationFailure]);

    const handlePayment = useCallback((payment: PaymentRow, suboptionId?: number) => {
        if (mutating) {
            return;
        }

        setPendingPayment(payment.id);
        api.selectPayment(payment.id, suboptionId, !payment.available)
            .then((next) => applyState(next, 'payment'))
            .catch(mutationFailure)
            .finally(() => setPendingPayment(null));
    }, [api, applyState, mutationFailure, mutating]);

    const showSectionErrors = useCallback((sections: SectionErrors) => {
        const messages = [...sections.delivery, ...sections.payment];
        trackFunnel('CheckoutValidationError', { step: 'deliveryPayment', messages: messages.join(' | ') });
        setErrors([]);
        setSectionErrors(sections);
        // na mobilu jsou sloupce pod sebou — odscrolluj k prvnímu, kterému něco chybí
        const target = sections.delivery.length > 0 ? deliveryColRef : paymentColRef;
        requestAnimationFrame(() => target.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }, []);

    const handleContinue = useCallback(() => {
        // rozjetá změna výběru (vč. opakovaného pokusu po výpadku) — /continue by validoval starý výběr
        if (mutating) {
            return;
        }

        // nevybraná doprava / platba: hláška hned, bez zbytečného 422 ze serveru
        // (ostatní případy — pobočka, podvolba platby, košík — ověřuje jen server)
        const missing: SectionErrors = {
            delivery: state.deliveries.some((row) => row.selected) ? [] : [t('Vyberte prosím způsob dopravy')],
            payment: state.payments.some((payment) => payment.selected) ? [] : [t('Vyberte prosím způsob platby')],
        };
        if (missing.delivery.length > 0 || missing.payment.length > 0) {
            showSectionErrors(missing);

            return;
        }

        setContinuing(true);
        api.continueToNext()
            .then(({ redirect }) => {
                window.location.assign(redirect);
            })
            .catch((e: unknown) => {
                setContinuing(false);
                const sections = e instanceof ApiError ? parseSectionErrors(e.fields) : null;
                if (sections && (sections.delivery.length > 0 || sections.payment.length > 0)) {
                    showSectionErrors(sections);

                    return;
                }

                if (e instanceof ApiError && e.fields && Array.isArray((e.fields as any).errors)) {
                    const messages = (e.fields as any).errors as string[];
                    trackFunnel('CheckoutValidationError', { step: 'deliveryPayment', messages: messages.join(' | ') });
                    setErrors(messages);
                } else {
                    failure(e);
                }
                requestAnimationFrame(() => errorsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
            });
    }, [api, failure, mutating, showSectionErrors, state, t]);

    const busy = countryPending;

    return (
        <div className="dpr">
            {state.country.options ? (
                <div className="dpr-country">
                    <h2>{t('Delivery country* (required)')}</h2>
                    <div className="dpr-country__select">
                        <select
                            className="form-control"
                            value={state.country.id}
                            disabled={countryPending}
                            onChange={(e) => handleCountry(Number(e.target.value))}
                        >
                            {state.country.options.map((option) => (
                                <option key={option.id} value={option.id}>{t(option.name)}</option>
                            ))}
                        </select>
                        {countryPending ? <Spinner className="dpr-country__spinner" /> : null}
                    </div>
                    {state.country.nonDeliverableNote ? (
                        <p className="dpr-country__note">{state.country.nonDeliverableNote}</p>
                    ) : null}
                </div>
            ) : null}

            <fieldset className={`dpr-panel${busy ? ' dpr-panel--busy' : ''}`}>
                <div className="dpr-columns">
                    <div className={colClass(sectionErrors.delivery)} ref={deliveryColRef}>
                        <h2 className="dpr-col__title">{t('Doprava')}</h2>
                        <ColumnErrors messages={sectionErrors.delivery} />
                        <ul className="dpr-list">
                            {state.deliveries.map((row) => (
                                <OptionCard
                                    key={`${row.type}-${row.id}`}
                                    name={row.name}
                                    logoUrl={row.logoUrl}
                                    price={row.price}
                                    free={row.free}
                                    priceFrom={row.priceFrom}
                                    descriptionHtml={row.description}
                                    selected={row.selected}
                                    disabled={!row.available}
                                    unavailableHint={row.conflictsWith
                                        ? t('Nelze kombinovat s platbou %s').replace('%s', row.conflictsWith)
                                        : null}
                                    pending={pendingDelivery === row.id}
                                    onSelect={() => handleDelivery(row)}
                                >
                                    {row.selected && row.hasBranches ? (
                                        <div className="dpr-card__branch">
                                            {row.selectedBranch ? (
                                                <>
                                                    <span className="dpr-card__branch-name">{row.selectedBranch.name}</span>
                                                    <span className="dpr-card__branch-address">{row.selectedBranch.address.join(', ')}</span>
                                                </>
                                            ) : (
                                                <span className="dpr-card__branch-name">{t('Nemáte vybrané žádné výdejní místo.')}</span>
                                            )}
                                            <button type="button" className="dpr-card__branch-change" onClick={() => setModal({ row })}>
                                                {row.selectedBranch ? t('Select different branch') : t('Vybrat výdejní místo')}
                                            </button>
                                        </div>
                                    ) : null}
                                </OptionCard>
                            ))}
                        </ul>
                        <p
                            className="dpr-note"
                            dangerouslySetInnerHTML={{
                                __html: t('Your order will be <strong>shipped the same day</strong> if it is placed <strong>before 11:30 AM</strong>'),
                            }}
                        />
                    </div>

                    <div className={colClass(sectionErrors.payment)} ref={paymentColRef}>
                        <h2 className="dpr-col__title">{t('Platba')}</h2>
                        <ColumnErrors messages={sectionErrors.payment} />
                        <ul className="dpr-list">
                            {state.payments.map((payment) => (
                                <OptionCard
                                    key={payment.id}
                                    name={payment.name}
                                    logoUrl={payment.logoUrl}
                                    price={payment.price}
                                    free={payment.free}
                                    descriptionHtml={payment.description}
                                    selected={payment.selected}
                                    disabled={!payment.available}
                                    unavailableHint={payment.conflictsWith
                                        ? t('Nelze kombinovat s dopravou %s').replace('%s', payment.conflictsWith)
                                        : null}
                                    pending={pendingPayment === payment.id}
                                    onSelect={() => handlePayment(payment)}
                                >
                                    {payment.suboptions.length > 0 ? (
                                        <ul className="dpr-suboptions">
                                            {payment.suboptions.map((suboption) => (
                                                <li key={suboption.id}>
                                                    <button
                                                        type="button"
                                                        className={`dpr-suboption${suboption.selected ? ' dpr-suboption--selected' : ''}`}
                                                        onClick={() => handlePayment(payment, suboption.id)}
                                                    >
                                                        {suboption.name}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : null}
                                </OptionCard>
                            ))}
                        </ul>
                    </div>
                </div>
            </fieldset>

            {errors.length > 0 ? (
                <div className="dpr-errors" ref={errorsRef} role="alert">
                    {errors.map((error) => <p key={error} className="msg msg--error">{error}</p>)}
                </div>
            ) : null}

            <div className="dpr-actions">
                <a className="btn btn-default btn-sm" href={props.links.back}>← {t('zpět')}</a>
                <button
                    type="button"
                    className="btn btn--custom-1 btn--basket dpr-actions__continue"
                    disabled={continuing || mutating}
                    onClick={handleContinue}
                >
                    {continuing ? <Spinner className="dpr-btn-spinner" /> : null}
                    <span>{t('Enter delivery data →')}</span>
                </button>
            </div>

            {modal ? (
                <BranchModal
                    title={modal.row.name}
                    deliveryId={modal.row.type === 'delivery' ? modal.row.id : undefined}
                    selectedBranchId={null}
                    api={api}
                    googleMapsApiKey={props.googleMapsApiKey}
                    language={props.language}
                    countryCode={state.country.code}
                    fallbackLogoUrl={modal.row.logoUrl}
                    confirming={confirmingBranch}
                    onConfirm={handleBranchConfirm}
                    onClose={() => setModal(null)}
                />
            ) : null}
        </div>
    );
}

function colClass(messages: string[]): string {
    return `dpr-col${messages.length > 0 ? ' dpr-col--missing' : ''}`;
}

/** hláška přímo pod nadpisem sloupce, kterému chybí výběr */
function ColumnErrors({ messages }: { messages: string[] }) {
    if (messages.length === 0) {
        return null;
    }

    return (
        <div className="dpr-col__errors" role="alert">
            {messages.map((message) => <p key={message} className="msg msg--error">{message}</p>)}
        </div>
    );
}
